import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { HiOutlineShieldExclamation, HiOutlineShieldCheck } from "react-icons/hi2";
import {
    Field,
    FormInput,
    FormTextarea,
    FormSwitch,
    SwitchGroup,
    Grid2,
    CollapsibleSection,
    ItemCard,
    ItemHeader,
    AddButton,
    Divider,
    SubsectionTitle,
    WeightField,
    AdditionalInfoField,
    isFieldInvalid,
    isFieldWarning,
} from "../ui";
import { createEmptyRiskFactor, createEmptyProtectiveEquipment } from "@/models/jobOffer";
import { ScoringConfigPanel, SCORING_ENABLED } from "@/scoringUi";
import { buildScoringLabels } from "../scoringLabels";

const WORK_CONDITION_KEYS = [
    "physicalWork",
    "workingAtHeight",
    "workingWithMachinery",
    "nightWork",
    "outdoorWork",
    "drivingVehicles",
    "chemicalSubstancesContact",
    "noiseExposure",
    "dustExposure",
    "customerContact",
];

const EQUIPMENT_EXAMPLE_KEYS = ["workClothing", "safetyFootwear", "helmet", "safetyGlasses", "gloves", "mask", "hearingProtection"];

const Step3Safety = ({ formik, warningErrors, visited, forceOpen }) => {
    const { t } = useTranslation("jobOffer");
    const { values, setFieldValue } = formik;
    const { workConditions, riskFactors, protectiveEquipment } = values;
    const invalid = (path) => isFieldInvalid(formik, path);
    const warn = (path) => isFieldWarning(warningErrors, visited, path);
    const sectionInfo = values.sectionAdditionalInfo ?? {};
    const setSectionInfo = (key, val) => setFieldValue(`sectionAdditionalInfo.${key}`, val);
    const scoringLabels = buildScoringLabels(t);

    const toggleCondition = (key) => setFieldValue(`workConditions.${key}`, !workConditions[key]);

    const updateRiskFactor = (i, updates) =>
        setFieldValue("riskFactors", riskFactors.map((r, idx) => (idx === i ? { ...r, ...updates } : r)));
    const removeRiskFactor = (i) => setFieldValue("riskFactors", riskFactors.filter((_, idx) => idx !== i));

    const updateEquipment = (i, updates) =>
        setFieldValue("protectiveEquipment", protectiveEquipment.map((e, idx) => (idx === i ? { ...e, ...updates } : e)));
    const removeEquipment = (i) => setFieldValue("protectiveEquipment", protectiveEquipment.filter((_, idx) => idx !== i));

    const addEquipmentExample = (name) => {
        const existing = protectiveEquipment.find((e) => e.name.toLowerCase() === name.toLowerCase());
        if (!existing) {
            const item = createEmptyProtectiveEquipment();
            item.name = name;
            setFieldValue("protectiveEquipment", [...protectiveEquipment, item]);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={6}>
            {/* Section 7: Safety & work conditions */}
            <CollapsibleSection
                number={7}
                title={t("step3.safety.title")}
                subtitle={t("step3.safety.subtitle")}
                icon={HiOutlineShieldExclamation}
                defaultOpen
                forceOpen={forceOpen}
            >
                <SubsectionTitle>{t("step3.safety.conditionsTitle")}</SubsectionTitle>
                <Box display="grid" gridTemplateColumns={{ base: "1fr 1fr", sm: "repeat(3, 1fr)" }} gap={3} mb={5}>
                    {WORK_CONDITION_KEYS.map((key) => {
                        const checked = workConditions[key];
                        return (
                            <Box
                                key={key}
                                as="button"
                                type="button"
                                onClick={() => toggleCondition(key)}
                                display="flex"
                                alignItems="center"
                                gap={2.5}
                                px={3}
                                py={2.5}
                                borderRadius={8}
                                borderWidth={1}
                                fontSize="sm"
                                textAlign="left"
                                borderColor={checked ? "purple.500" : "border.primary"}
                                bg={checked ? "purple.900" : "bg.secondary"}
                                color={checked ? "purple.300" : "text.grey"}
                                transition="colors 0.15s"
                            >
                                <Box
                                    w={4}
                                    h={4}
                                    borderRadius={4}
                                    borderWidth={1}
                                    borderColor={checked ? "purple.500" : "border.secondary"}
                                    bg={checked ? "purple.500" : "transparent"}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                >
                                    {checked && <Text color="white" fontSize="10px">✓</Text>}
                                </Box>
                                {t(`step3.workConditions.${key}`)}
                            </Box>
                        );
                    })}
                </Box>

                <Field label={t("step3.safety.otherConditions")}>
                    <FormInput
                        value={workConditions.other}
                        onChange={(e) => setFieldValue("workConditions.other", e.target.value)}
                        placeholder={t("step3.safety.otherConditionsPlaceholder")}
                    />
                </Field>

                <Divider />

                <SubsectionTitle>{t("step3.safety.riskFactorsTitle")}</SubsectionTitle>
                <Box display="flex" flexDirection="column" gap={3}>
                    {riskFactors.map((factor, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step3.safety.riskFactorItemTitle")} index={i} onRemove={() => removeRiskFactor(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Field label={t("step3.safety.riskFactorName")} required invalid={invalid(`riskFactors.${i}.name`)} warning={warn(`riskFactors.${i}.name`)}>
                                    <FormInput
                                        value={factor.name}
                                        onChange={(e) => updateRiskFactor(i, { name: e.target.value })}
                                        placeholder={t("step3.safety.riskFactorNamePlaceholder")}
                                    />
                                </Field>
                                <Field label={t("step3.safety.description")}>
                                    <FormTextarea
                                        value={factor.description}
                                        onChange={(e) => updateRiskFactor(i, { description: e.target.value })}
                                        placeholder={t("step3.safety.descriptionPlaceholder")}
                                        rows={2}
                                    />
                                </Field>
                                {false && (
                                    <Grid2>
                                        <WeightField value={factor.weight} onChange={(v) => updateRiskFactor(i, { weight: v })} />
                                    </Grid2>
                                )}
                                <SwitchGroup>
                                    <FormSwitch label={t("step3.safety.aiShouldAsk")} checked={factor.aiShouldAsk} onChange={(v) => updateRiskFactor(i, { aiShouldAsk: v })} />
                                    <FormSwitch label={t("step3.safety.refusalIsEliminating")} checked={factor.refusalIsEliminating} onChange={(v) => updateRiskFactor(i, { refusalIsEliminating: v })} />
                                </SwitchGroup>
                                {SCORING_ENABLED && (
                                    <>
                                        <Divider my={1} />
                                        <ScoringConfigPanel
                                            config={factor.scoring}
                                            onChange={(scoring) => updateRiskFactor(i, { scoring })}
                                            labels={scoringLabels}
                                        />
                                    </>
                                )}
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step3.safety.addRiskFactor")} onClick={() => setFieldValue("riskFactors", [...riskFactors, createEmptyRiskFactor()])} />
                </Box>
                <Box mt={4}>
                    <AdditionalInfoField value={sectionInfo.safetyAndConditions} onChange={(e) => setSectionInfo("safetyAndConditions", e.target.value)} />
                </Box>
            </CollapsibleSection>

            {/* Section 8: Protective equipment */}
            <CollapsibleSection
                number={8}
                title={t("step3.equipment.title")}
                subtitle={t("step3.equipment.subtitle")}
                icon={HiOutlineShieldCheck}
                forceOpen={forceOpen}
            >
                <Box mb={4}>
                    <Text fontSize="xs" color="text.grey" mb={2}>{t("step3.equipment.quickAdd")}</Text>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {EQUIPMENT_EXAMPLE_KEYS.map((key) => {
                            const label = t(`step3.equipmentExamples.${key}`);
                            return (
                                <Box
                                    key={key}
                                    as="button"
                                    type="button"
                                    onClick={() => addEquipmentExample(label)}
                                    px={2.5}
                                    py={1}
                                    borderRadius={6}
                                    bg="bg.secondary"
                                    border="1px solid"
                                    borderColor="border.primary"
                                    fontSize="xs"
                                    color="text.grey"
                                    _hover={{ borderColor: "purple.500", color: "purple.400" }}
                                >
                                    + {label}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={3}>
                    {protectiveEquipment.map((item, i) => (
                        <ItemCard key={i}>
                            <ItemHeader title={t("step3.equipment.itemTitle")} index={i} badge={item.name || undefined} onRemove={() => removeEquipment(i)} />
                            <Box display="flex" flexDirection="column" gap={3}>
                                <Grid2>
                                    <Field label={t("step3.equipment.name")} required invalid={invalid(`protectiveEquipment.${i}.name`)} warning={warn(`protectiveEquipment.${i}.name`)}>
                                        <FormInput
                                            value={item.name}
                                            onChange={(e) => updateEquipment(i, { name: e.target.value })}
                                            placeholder={t("step3.equipment.namePlaceholder")}
                                        />
                                    </Field>
                                    {false && (
                                        <WeightField value={item.weight} onChange={(v) => updateEquipment(i, { weight: v })} />
                                    )}
                                </Grid2>
                                <Field label={t("step3.equipment.aiQuestion")}>
                                    <FormInput
                                        value={item.aiQuestion}
                                        onChange={(e) => updateEquipment(i, { aiQuestion: e.target.value })}
                                        placeholder={t("step3.equipment.aiQuestionPlaceholder")}
                                    />
                                </Field>
                                <SwitchGroup>
                                    <FormSwitch label={t("step3.equipment.providedByEmployer")} checked={item.providedByEmployer} onChange={(v) => updateEquipment(i, { providedByEmployer: v })} />
                                    <FormSwitch label={t("step3.equipment.requiredFromEmployee")} checked={item.requiredFromEmployee} onChange={(v) => updateEquipment(i, { requiredFromEmployee: v })} />
                                    <FormSwitch label={t("step3.equipment.mandatoryForPosition")} checked={item.mandatoryForPosition} onChange={(v) => updateEquipment(i, { mandatoryForPosition: v })} />
                                    <FormSwitch label={t("step3.safety.refusalIsEliminating")} checked={item.refusalIsEliminating} onChange={(v) => updateEquipment(i, { refusalIsEliminating: v })} />
                                </SwitchGroup>
                            </Box>
                        </ItemCard>
                    ))}
                    <AddButton label={t("step3.equipment.add")} onClick={() => setFieldValue("protectiveEquipment", [...protectiveEquipment, createEmptyProtectiveEquipment()])} />
                    <AdditionalInfoField value={sectionInfo.protectiveEquipment} onChange={(e) => setSectionInfo("protectiveEquipment", e.target.value)} />
                </Box>
            </CollapsibleSection>
        </Box>
    );
};

export default Step3Safety;
